import React from "react";
import { css, useTheme } from "@emotion/react";

import Dialog from "core/Components/Dialog/Dialog";
import DialogContent from "core/Components/Dialog/DialogContent";
import { NOOP } from "core/constants";
import ContentCombine from "core/Components/ContentCombine";
import { ReactComponent as TickSVG } from 'images/icons/green-tick.svg';
import { ReactComponent as EmailSVG } from 'images/icons/email.svg';
import { ReactComponent as ExclamationError } from 'images/icons/exclamation-error.svg';
import Cart from 'core/Components/Icons/Cart';
import RoundButton from "core/Components/Buttons/RoundButton";

type Props = {
    className?: string,
    heading?: string,
    subheading?: string,
    bottomColour?: string,
    bottomSectionMessageOne?: string,
    bottomSectionMessageTwo?: string,
    buttonText?: string,
    buttonTo?: string,
    circleIcon?: 'email'|'tick'|'exclamation',
    onClickButton?: () => void,
    closeTo?: string,
    onClose?: () => void,
};

const CheckoutFinishDialog: React.FC<Props> = (
    {
        className = '',
        heading = '',
        subheading = '',
        bottomColour = null,
        bottomSectionMessageOne = '',
        bottomSectionMessageTwo = '',
        buttonText = '',
        buttonTo = '/classroom-resources',
        circleIcon = 'tick',
        onClickButton = NOOP,
        closeTo = '',
        onClose = NOOP,
    }
) => {
    const theme = useTheme();

    const style = css`
        max-width: 445px;
        width: 445px;
        min-height: 450px;
        padding: 0 !important;
        display: flex;
        justify-content: center;

        ${theme.breakpoints.only('xl')} {
            max-width: 350px;
            min-height: 341px;
        }

        ${theme.breakpoints.down('lg')} {
            max-width: 337px;
            min-height: 341px;
        }
    `;

    const dialogContentStyle = css`
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
    `;

    const topContainerStyle = css`
        height: 58%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 27px 0 12px;

        ${theme.breakpoints.down('xl')} {
            padding: 20px 0 8px;
        }
    `;

    const bottomContainerStyle = css`
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
        align-items: center;
        flex: 1;
        padding: 25px 0 30px;
        text-align: left;
        background-color: ${bottomColour ?? theme.colours.java};
        border-bottom-right-radius: 25px;
        border-bottom-left-radius: 25px;

        ${theme.breakpoints.down('xl')} {
            padding: 17px 0 23px;
        }
    `;

    const bottomMessagesStyle = ({ isFirst = false } = {}) => css`
        width: 264px;
        text-align: center;
        color: white;
        font-weight: ${isFirst ? theme.fonts.weights.bold : theme.fonts.weights.light};
    `;

    const buttonGroupStyle = css`
        display: flex;
        flex-direction: column;
        gap: 11px;
        margin-top: 25px;

        ${theme.breakpoints.down('xl')} {
            margin-top: 19px;
        }

        ${theme.breakpoints.down('sm')} {
            gap: 8px;
        }
    `;

    const buttonStyle = css`
        position: relative;
        width: 308px;
        height: 60px;
        border-radius: 30px;
        font-size: 20px;
        display: flex;
        align-items: center;
        background-color: white;
        cursor: pointer;
        transition: 300ms background-color ease;
        box-shadow: ${theme.borderAndShadow.boxShadow3};

        :hover {
            div {
                color: black;
            }
        }

        ${theme.breakpoints.down('xl')} {
            width: 233px;
            height: 45px;
        }
    `;

    const iconsContainerStyle = css`
        position: relative;
        width: 168px;
        height: 124px;
        margin-bottom: 20px;

        ${theme.breakpoints.down('xl')} {
            width: 125px;
            height: 92px;
            margin-bottom: 10px;
        }
    `;

    const tickSvgStyle = css`
        position: absolute;
        left: 0;
        top: 0;
        width: 56px;
        height: 56px;
        z-index: 3;

        ${theme.breakpoints.down('xl')} {
            height: 47%;
        }
    `;

    const cartStyle = css`
        position: absolute;
        left: 23%;
        top: 12%;
        height: 97px;
        z-index: 2;

        ${theme.breakpoints.down('xl')} {
            height: 86%;
        }

        svg {
            width: 100%;
            height: 100%;
        }
    `;

    const circleStyle = css`
        position: absolute;
        top: 31%;
        left: 46%;
        width: 88px;
        height: 88px;
        background-color: ${theme.colours.gold};
        border-radius: 50%;

        ${theme.breakpoints.down('xl')} {
            width: 53%;
            height: 73.81%;
        }
    `;

    const headingStyle = css`
        color: ${theme.colours.curiousBlue};
        font-weight: ${theme.fonts.weights.bold};
    `;

    const subheadingStyle = css`
        color: ${theme.colours.shipGray};
        font-weight: ${theme.fonts.weights.light};

        ${theme.breakpoints.only('xl')} {
            margin-top: 4px;
        }

        ${theme.breakpoints.down('lg')} {
            margin-top: 8px;
        }
    `;

    return (
        <Dialog
            extraStyle={style}
            closeButtonVariant={'circle-overflowing'}
            onClose={onClose}
            closeTo={closeTo}
            className={className}
        >
            <DialogContent css={dialogContentStyle} center>
                <div css={topContainerStyle}>
                    <div css={iconsContainerStyle}>
                        {circleIcon === 'tick' && (
                            <TickSVG css={tickSvgStyle} />
                        )}
                        {circleIcon === 'email' && (
                            <EmailSVG css={tickSvgStyle} />
                        )}
                        {circleIcon === 'exclamation' && (
                            <ExclamationError css={tickSvgStyle} />
                        )}
                        <Cart css={cartStyle} />
                        <div css={circleStyle} />
                    </div>
                    <ContentCombine
                        css={headingStyle}
                        fontSizeType={'3'}
                    >
                        {heading}
                    </ContentCombine>
                    <ContentCombine
                        css={subheadingStyle}
                        fontSizeType={'18'}
                    >
                        {subheading}
                    </ContentCombine>
                </div>
                <div css={bottomContainerStyle}>
                    <div className={'bottom-section-messages'}>
                        {bottomSectionMessageOne && (
                            <ContentCombine
                                css={bottomMessagesStyle({ isFirst: true })}
                                fontSizeType={'18'}
                            >
                                {bottomSectionMessageOne}
                            </ContentCombine>
                        )}
                        <ContentCombine
                            css={bottomMessagesStyle()}
                            fontSizeType={'18'}
                        >
                            {bottomSectionMessageTwo}
                        </ContentCombine>
                    </div>
                    <div css={buttonGroupStyle}>
                        <RoundButton
                            linkURL={buttonTo}
                            css={buttonStyle}
                            onButtonClick={onClickButton}
                            textColour={'white'}
                        >
                            <ContentCombine fontSizeType={'28'}>
                                {buttonText}
                            </ContentCombine>
                        </RoundButton>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default CheckoutFinishDialog;